import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './HomePage.css';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import ConsultantCardGrid from '../booking/components/ConsultantCardGrid';
import { useChat } from '../../contexts/ChatContext';
import { resolveHomeItemClick } from './utils/homeItemClick';
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { HeroSection } from './components/HeroSection';
import { HomeSearchBar } from './components/HomeSearchBar';
import { LegalImmigrationQuickSection } from './components/LegalImmigrationQuickSection';
import { TowingAssistanceQuickSection } from './components/TowingAssistanceQuickSection';
import { AccountingFinancialQuickSection } from './components/AccountingFinancialQuickSection';
import { VehicleMarketplaceQuickSection } from './components/VehicleMarketplaceQuickSection';
import { ProfessionalHelpQuickSection } from './components/ProfessionalHelpQuickSection';
import { CmsFlexibleHomeSection } from './components/CmsFlexibleHomeSection';
import { BUILTIN_HOME_QUICK_SECTION_IDS } from './constants/homeCardLayouts';
import { ServiceCard } from './components/ServiceCard';
import { useAppHomeContent } from '../../hooks/useAppHomeContent';
import { useClientCountry } from '../../hooks/useClientCountry';
import { HOME_TOPIC_SECTIONS } from './homeTopicSections';



export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { country } = useClientCountry();
  const { startTopicChat } = useChat();
  const { sections, storeServices, takers, carouselSlides, storeLoading, sectionsLoading, consultantsLoading } =
    useAppHomeContent();
  /** Keep built-in tiles on screen while CMS loads so the homepage is not empty. */
  const cmsSections =
    sections.length > 0 ? sections : !sectionsLoading && country === 'IN' ? [] : HOME_TOPIC_SECTIONS;
  const openServiceChat = useCallback((categoryId: string, slug: string) => {
    const next = resolveHomeItemClick(categoryId, slug, cmsSections, country);
    if (!next) return;
    if (next.type === 'page') {
      navigate(next.path);
      return;
    }
    startTopicChat(next.topic);
    navigate('/chat');
  }, [cmsSections, country, navigate, startTopicChat]);

  const section2Ref = useScrollAnimation();
  const section3Ref = useScrollAnimation();


  // "View all" coming-soon popup for sections without a live page yet
  type ComingSoonSection = 'store' | 'service' | 'consultant-experts' | 'consultant' | null;
  const [comingSoonSection, setComingSoonSection] = useState<ComingSoonSection>(null);
  const comingSoonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (comingSoonSection) {
      const t = setTimeout(() => setComingSoonSection(null), 3000);
      return () => clearTimeout(t);
    }
  }, [comingSoonSection]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (comingSoonRef.current && !comingSoonRef.current.contains(e.target as Node)) {
        setComingSoonSection(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    const topic = params.get('topic');
    if (!section) return;

    const timer = window.setTimeout(() => {
      document.getElementById(`home-section-${section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (topic) {
        const [categoryId, slug] = topic.split(':');
        if (categoryId && slug) openServiceChat(categoryId, slug);
      }
    }, 350);

    return () => window.clearTimeout(timer);
  }, [location.search, openServiceChat]);

  return (
    <div className="cwy-dashboard-page">
      <div className="home-hero-bleed">
        <HeroSection slides={carouselSlides.length > 0 ? carouselSlides : undefined} />
      </div>

      <div className="home-below-hero">
        <HomeSearchBar
          topicSections={sections.length > 0 ? sections : undefined}
          storeServices={storeServices}
          consultants={takers}
          onTopicSelect={openServiceChat}
        />

        <div className="home-quick-paired-rows">
          <LegalImmigrationQuickSection onSelect={openServiceChat} topicSections={cmsSections} />
          <TowingAssistanceQuickSection onSelect={openServiceChat} topicSections={cmsSections} />
        </div>

        <div className="page-container">

        {storeServices.length > 0 && (
          <section
            id="home-section-store"
            className="home-cwy-store-block"
            aria-labelledby="home-cwy-store-title"
          >
            <motion.div
              className="saas-sec-head home-cwy-store-head"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="saas-sec-head-left">
                <span className="saas-sec-eyebrow">
                  <span className="saas-sec-eyebrow-line" />
                  CWY Store
                </span>
                <h2 id="home-cwy-store-title" className="saas-sec-title">
                  Services built for your goals
                </h2>
                <p className="saas-sec-sub">
                  Instant access to certified courses, licensing help, and professional services.
                </p>
              </div>
              <button className="saas-sec-view-all" type="button" onClick={() => navigate('/store')}>
                View all <i className="ti ti-arrow-right" style={{ fontSize: 13 }} />
              </button>
            </motion.div>

            <div className="catalog-grid home-cwy-store-grid">
              {storeServices.map((svc, i) => (
                <div key={`store-${svc.slug}`} className="catalog-grid-item">
                  <ServiceCard service={svc} index={i} />
                </div>
              ))}
            </div>
          </section>
        )}

        {storeLoading && storeServices.length === 0 && (
          <div className="home-cwy-store-loading">Loading store…</div>
        )}

        <AccountingFinancialQuickSection onSelect={openServiceChat} topicSections={cmsSections} />
        {country === 'CA' && (
          <VehicleMarketplaceQuickSection onSelect={openServiceChat} topicSections={cmsSections} />
        )}
        <ProfessionalHelpQuickSection onSelect={openServiceChat} topicSections={cmsSections} />

        <section
          id="home-section-consultants"
          className="home-section-stack home-consultants-block"
          aria-labelledby="home-consultants-title"
        >
          <motion.div
            className="saas-sec-head"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="saas-sec-head-left">
              <span className={`saas-sec-eyebrow${country === 'IN' ? ' saas-sec-eyebrow--india' : ''}`}>
                <span className="saas-sec-eyebrow-line" />
                {country === 'IN' ? 'India consultants' : 'Consultants'}
              </span>
              <h2
                id="home-consultants-title"
                ref={section2Ref as React.RefObject<HTMLHeadingElement>}
                className="saas-sec-title"
              >
                Meet our experts
              </h2>
              <p className="saas-sec-sub">
                {country === 'IN'
                  ? 'Legal & immigration experts based in India — view a profile and book a walk-in or call today.'
                  : 'Skip the wait — click any card to view their profile and book a slot instantly.'}
              </p>
            </div>
          </motion.div>

          <div className="home-section-body">
            {consultantsLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Loading consultants...</div>
            ) : takers.length > 0 ? (
              <ConsultantCardGrid takers={takers} />
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No consultants available for your region yet.</div>
            )}
          </div>
        </section>

        <div ref={comingSoonRef} className="home-cms-quick-sections">
          {sectionsLoading && sections.length === 0 && country !== 'CA' ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: '#666' }}>Loading services…</div>
          ) : sections.length > 0 ? (
            sections
              .filter((section) => !BUILTIN_HOME_QUICK_SECTION_IDS.has(section.id))
              .map((section, index) => (
                <CmsFlexibleHomeSection
                  key={section.id}
                  section={section}
                  sectionIndex={index}
                  titleRef={index === 0 ? (section3Ref as React.RefObject<HTMLHeadingElement>) : undefined}
                  comingSoonSection={comingSoonSection}
                  onViewAll={() => setComingSoonSection(section.comingSoonKey)}
                  onSelect={openServiceChat}
                />
              ))
          ) : (
            <div style={{ padding: '20px 0', textAlign: 'center', color: '#666' }}>
              No services are available for your region right now.
            </div>
          )}
        </div>

        </div>
      </div>

    </div>
  );
};
